import { DataList, type TableColumn, SectionHeader, Pagination } from '@core/components';
import { IconButton, Info16, IconButtonSize, IconSize, Badge, Shimmer } from '@soteria/design-system-ve';
import type { StandardLabel } from '@system-config/label-management/domain';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { useState, useMemo } from 'react';

import { LabelsMoreInfoDialog } from './LabelsMoreInfoDialog';
import * as styles from './LabelsSection.css';

const ITEMS_PER_PAGE = 5;
const SHIMMER_SECTIONS = 2; // Number of content type sections to show while loading

// Helper function to convert content type to Title Case
const toTitleCase = (str: string): string =>
  str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export function LabelsSection() {
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);
  // Track current page for each content type
  const [currentPages, setCurrentPages] = useState<Map<string, number>>(new Map());

  const { labels, loading, errors } = useStandardLabels();

  // Group labels by content type
  const labelsByContentType = useMemo(() => {
    const grouped = new Map<string, StandardLabel[]>();

    labels.forEach(label => {
      const contentType = label.contentType || 'Other';
      const existing = grouped.get(contentType) || [];

      grouped.set(contentType, [...existing, label]);
    });

    // Sort content types alphabetically
    return new Map([...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  }, [labels]);

  // Columns without content type since it's now the section header
  const columns: TableColumn<StandardLabel>[] = [
    {
      key: 'labelName',
      header: 'Label Name',
      render: item => item.labelName,
      width: '60%',
    },
    {
      key: 'labelStatus',
      header: 'Status',
      render: item => {
        let badgeClass;

        if (item.labelStatus.toLowerCase() === 'approved') {
          badgeClass = styles.approvedBadge;
        } else if (item.labelStatus.toLowerCase() === 'pending') {
          badgeClass = styles.pendingBadge;
        }

        return <Badge className={badgeClass || undefined} text={item.labelStatus} />;
      },
      width: '40%',
    },
  ];

  return (
    <>
      {loading && labels.length === 0 ? (
        <div className={styles.shimmerHeaderContainer}>
          <Shimmer className={styles.shimmerTitleBlock} />
          <Shimmer className={styles.shimmerDescriptionBlock} />
        </div>
      ) : (
        <SectionHeader
          description="Predefined content classification categories for consistent content management across your communities."
          endComponent={
            <IconButton
              aria-label="Learn more about standard labels"
              buttonSize={IconButtonSize.Medium}
              icon={Info16}
              iconSize={IconSize.XSmall}
              onClick={() => setIsMoreInfoDialogOpen(true)}
            />
          }
          title={`Standard Labels (${labels.length})`}
        />
      )}

      {/* Show shimmer sections while loading */}
      {loading && labels.length === 0
        ? Array.from({ length: SHIMMER_SECTIONS }, (_, index) => (
            <div key={`shimmer-section-${index}`} className={styles.shimmerSectionContainer}>
              <Shimmer className={styles.shimmerSectionTitle} />
              <DataList isLoading columns={columns} data={[]} dataTypeName="standard label" loadingRowCount={ITEMS_PER_PAGE} />
            </div>
          ))
        : /* Standard Labels Lists - Grouped by Content Type */
          Array.from(labelsByContentType.entries()).map(([contentType, contentTypeLabels]) => {
            const currentPage = currentPages.get(contentType) || 1;
            const totalPages = Math.ceil(contentTypeLabels.length / ITEMS_PER_PAGE);
            const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
            const endIndex = startIndex + ITEMS_PER_PAGE;
            const paginatedLabels = contentTypeLabels.slice(startIndex, endIndex);

            const handlePageChange = (page: number) => {
              setCurrentPages(prev => new Map(prev).set(contentType, page));
            };

            return (
              <div key={contentType} className={totalPages > 1 ? styles.contentTypeSectionWithPagination : styles.contentTypeSection}>
                <SectionHeader title={`${toTitleCase(contentType)} (${contentTypeLabels.length})`} />
                <DataList columns={columns} data={paginatedLabels} dataTypeName="standard label" error={errors} isLoading={loading} />
                {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />}
              </div>
            );
          })}

      {/* More Info Dialog */}
      <LabelsMoreInfoDialog isOpen={isMoreInfoDialogOpen} onClose={() => setIsMoreInfoDialogOpen(false)} />
    </>
  );
}
